// Fornecedores.js — v5
// ==============================================
// ✅ Modal de detalhes com dashboard completo
// ✅ Edição de compras com regras de editabilidade
// ✅ Botão ✏️ só aparece se backend permitir (podeEditar)
// ✅ Ícone 🔒 em compras bloqueadas
// ✅ Badge "Sem NF" em compras pendentes
// ✅ 🔥 Paginação de produtos associados
// ✅ 🔥 Paginação de compras
// ✅ 🔥 Stock sempre atualizado (recarrega dashboard)
// ==============================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
    BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './Fornecedores.css';
import { Icon } from './SharedComponents';

const API_BASE_URL = '/api';

const CORES_CATEGORIAS = [
    '#3b82f6', '#8b5cf6', '#f59e0b', '#10b981',
    '#ef4444', '#06b6d4', '#ec4899', '#64748b'
];

// 🔥 Items por página nas tabelas do dashboard
const PRODUTOS_POR_PAGINA = 5;
const COMPRAS_POR_PAGINA = 5;

export default function Fornecedores() {
    // ==============================================
    // ESTADOS PRINCIPAIS
    // ==============================================
    const [fornecedores, setFornecedores] = useState([]);
    const [filteredFornecedores, setFilteredFornecedores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showEditCompraModal, setShowEditCompraModal] = useState(false);
    const [fornecedorToDelete, setFornecedorToDelete] = useState(null);
    const [fornecedorDetalhes, setFornecedorDetalhes] = useState(null);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 9,
        total: 0,
        pages: 0
    });
    const [isSuperadmin, setIsSuperadmin] = useState(false);
    const [loggedUser, setLoggedUser] = useState(null);
    const [empresaId, setEmpresaId] = useState(null);
    const [empresaNome, setEmpresaNome] = useState('');

    // ==============================================
    // DASHBOARD DO FORNECEDOR
    // ==============================================
    const [dashboardFornecedor, setDashboardFornecedor] = useState(null);
    const [loadingDashboard, setLoadingDashboard] = useState(false);
    const [dashboardErro, setDashboardErro] = useState(null);

    // 🔥 Paginação do dashboard
    const [paginaProdutos, setPaginaProdutos] = useState(1);
    const [paginaCompras, setPaginaCompras] = useState(1);

    // ==============================================
    // EDIÇÃO DE COMPRA
    // ==============================================
    const [compraEditando, setCompraEditando] = useState(null);
    const [editCompraForm, setEditCompraForm] = useState({
        documentoFiscal: '',
        metodoPagamento: '',
        referenciaPagamento: '',
        observacoes: '',
        confirmarPagamento: false
    });
    const [editCompraLoading, setEditCompraLoading] = useState(false);
    const [editCompraMsg, setEditCompraMsg] = useState({ text: '', type: '' });

    const [createLoading, setCreateLoading] = useState(false);
    const [createMessage, setCreateMessage] = useState({ text: '', type: '' });
    const [editLoading, setEditLoading] = useState(false);
    const [editMessage, setEditMessage] = useState({ text: '', type: '' });

    // ==============================================
    // ESTATÍSTICAS
    // ==============================================
    const [stats, setStats] = useState({
        total: 0,
        ativos: 0,
        inativos: 0,
        comEmail: 0,
        comTelefone: 0,
        comNuit: 0,
        comCidade: 0,
        comProvincia: 0,
        comEndereco: 0
    });

    // ==============================================
    // FILTROS
    // ==============================================
    const [filtros, setFiltros] = useState({
        status: 'todos',
        cidade: '',
        provincia: ''
    });
    const [showFiltros, setShowFiltros] = useState(false);

    // ==============================================
    // FORM DATA
    // ==============================================
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        endereco: '',
        nuit: '',
        telefone: '',
        cidade: '',
        provincia: '',
        ativo: true
    });

    const [editFormData, setEditFormData] = useState(null);

    // ==============================================
    // API
    // ==============================================
    const api = useMemo(() => axios.create({
        baseURL: '/api',
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache'
        }
    }), []);

    // ==============================================
    // FORMATADORES
    // ==============================================
    const formatarMoeda = useCallback((valor) => {
        if (valor === undefined || valor === null || isNaN(valor)) {
            return '0,00 MT';
        }
        return new Intl.NumberFormat('pt-PT', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(valor) + ' MT';
    }, []);

    const formatarData = useCallback((data) => {
        if (!data) return '—';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '—';
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const ano = d.getFullYear();
        return `${dia}/${mes}/${ano}`;
    }, []);

    // ==============================================
    // HELPERS
    // ==============================================
    const extrairEmpresaId = (empresa) => {
        if (!empresa) return null;
        if (typeof empresa === 'string') return empresa;
        if (typeof empresa === 'object' && empresa._id) return empresa._id;
        if (typeof empresa === 'object' && empresa.id) return empresa.id;
        return null;
    };

    const obterEmpresaIdDoUtilizador = (user) => {
        if (!user) return null;
        let id = extrairEmpresaId(user.empresaId);
        if (id) return id;
        id = extrairEmpresaId(user.empresa);
        if (id) return id;
        id = extrairEmpresaId(user.empresaAtual);
        if (id) return id;
        if (user.empresas && Array.isArray(user.empresas) && user.empresas.length > 0) {
            id = extrairEmpresaId(user.empresas[0]);
            if (id) return id;
        }
        return null;
    };

    const obterEmpresaNome = (user) => {
        if (!user) return null;
        if (user.empresaId && typeof user.empresaId === 'object' && user.empresaId.nome) return user.empresaId.nome;
        if (user.empresaAtual && typeof user.empresaAtual === 'object' && user.empresaAtual.nome) return user.empresaAtual.nome;
        if (user.empresaNome) return user.empresaNome;
        if (user.empresa && typeof user.empresa === 'string') return user.empresa;
        return null;
    };

    const showTempToast = (title, message, type = 'success') => {
        setMessage({ text: `${title}: ${message}`, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    };

    // 🔥 Helper: título do modal de edição consoante o motivo
    const tituloModalEdicao = (compra) => {
        if (!compra) return 'Editar Compra';
        const motivo = compra.motivoBloqueio;
        if (motivo === 'sem_nf') return 'Adicionar Nota Fiscal';
        if (motivo === 'nf_sem_pagamento') return 'Confirmar Pagamento';
        return 'Editar Compra';
    };

    // 🔥 Helper: descrição do bloqueio
    const descricaoBloqueio = (compra) => {
        const motivo = compra?.motivoBloqueio;
        if (motivo === 'compra_anulada') return 'Compra anulada — não editável';
        if (motivo === 'compra_completa') return 'Compra completa (com NF e paga) — não editável';
        return 'Compra bloqueada';
    };

    // ==============================================
    // CALCULAR ESTATÍSTICAS
    // ==============================================
    const calcularStats = (fornecedoresData) => {
        const data = Array.isArray(fornecedoresData) ? fornecedoresData : [];
        const temValor = (campo) => {
            if (!campo) return false;
            if (typeof campo === 'string') return campo.trim().length > 0;
            return true;
        };

        setStats({
            total: data.length,
            ativos: data.filter(f => f.ativo !== false).length,
            inativos: data.filter(f => f.ativo === false).length,
            comEmail: data.filter(f => temValor(f.email)).length,
            comTelefone: data.filter(f => temValor(f.telefone)).length,
            comNuit: data.filter(f => temValor(f.nuit)).length,
            comCidade: data.filter(f => temValor(f.cidade)).length,
            comProvincia: data.filter(f => temValor(f.provincia)).length,
            comEndereco: data.filter(f => temValor(f.endereco)).length
        });
    };

    // ==============================================
    // BUSCAR DADOS
    // ==============================================
    const fetchFornecedores = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) { setLoading(false); return; }

            const response = await api.get('/fornecedores?limit=9999');
            if (response.data) {
                const fornecedoresData = Array.isArray(response.data)
                    ? response.data
                    : response.data.data || [];

                setFornecedores(fornecedoresData);
                setFilteredFornecedores(fornecedoresData);
                calcularStats(fornecedoresData);

                const pag = response.data.pagination;
                const total = pag?.total ?? fornecedoresData.length;

                setPagination(prev => {
                    const limit = prev.limit || 9;
                    return { page: 1, limit, total, pages: Math.ceil(total / limit) };
                });
            }
        } catch (err) {
            console.error('❌ Erro ao carregar fornecedores:', err);
            showTempToast('Erro', 'Não foi possível carregar os fornecedores', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const fetchLoggedUser = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
                if (storedUser && storedUser._id) {
                    setLoggedUser(storedUser);
                    setIsSuperadmin(storedUser.perfil === 'Superadmin');
                    setEmpresaId(obterEmpresaIdDoUtilizador(storedUser));
                    setEmpresaNome(obterEmpresaNome(storedUser) || 'N/A');
                    return storedUser;
                }
                return null;
            }

            const response = await axios.get(`${API_BASE_URL}/users/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                const userData = response.data.data;
                setLoggedUser(userData);
                setIsSuperadmin(userData.perfil === 'Superadmin');
                setEmpresaId(obterEmpresaIdDoUtilizador(userData));
                setEmpresaNome(obterEmpresaNome(userData) || 'N/A');
                localStorage.setItem('usuario', JSON.stringify(userData));
                return userData;
            }
        } catch (err) {
            console.error('❌ Erro ao buscar utilizador:', err);
        }
        return null;
    };

    // ==============================================
    // BUSCAR DASHBOARD
    // 🔥 Reset das páginas ao recarregar
    // ==============================================
    const fetchDashboardFornecedor = useCallback(async (fornecedorId, resetPages = true) => {
        setLoadingDashboard(true);
        setDashboardFornecedor(null);
        setDashboardErro(null);
        if (resetPages) {
            setPaginaProdutos(1);
            setPaginaCompras(1);
        }
        try {
            const token = localStorage.getItem('token');
            if (!token) { setDashboardErro('Sessão expirada.'); return; }

            const response = await api.get(`/fornecedores/${fornecedorId}/dashboard`);
            if (response.data?.success) {
                setDashboardFornecedor(response.data.data);
            } else {
                setDashboardErro(response.data?.erro || 'Resposta inválida do servidor');
            }
        } catch (err) {
            console.error('❌ Erro ao buscar dashboard:', err);
            setDashboardErro(err.response?.data?.erro || err.message || 'Erro ao carregar dashboard');
        } finally {
            setLoadingDashboard(false);
        }
    }, [api]);

    // ==============================================
    // FILTRAR
    // ==============================================
    useEffect(() => { filtrarFornecedores(); }, [searchTerm, fornecedores, filtros]);

    const filtrarFornecedores = () => {
        let resultados = [...fornecedores];
        if (searchTerm && searchTerm.trim() !== '') {
            const term = searchTerm.toLowerCase().trim();
            resultados = resultados.filter(f => {
                const campos = [f.nome, f.email, f.nuit, f.telefone, f.endereco, f.cidade, f.provincia];
                return campos.some(c => c && String(c).toLowerCase().includes(term));
            });
        }
        if (filtros.status && filtros.status !== 'todos') {
            const isAtivo = filtros.status === 'ativo';
            resultados = resultados.filter(f => (f.ativo !== false) === isAtivo);
        }
        if (filtros.cidade?.trim()) {
            const c = filtros.cidade.toLowerCase().trim();
            resultados = resultados.filter(f => (f.cidade || '').toLowerCase().includes(c));
        }
        if (filtros.provincia?.trim()) {
            const p = filtros.provincia.toLowerCase().trim();
            resultados = resultados.filter(f => (f.provincia || '').toLowerCase().includes(p));
        }

        setFilteredFornecedores(resultados);
        calcularStats(resultados);
        setPagination(prev => ({
            ...prev,
            total: resultados.length,
            pages: Math.ceil(resultados.length / prev.limit),
            page: 1
        }));
    };

    const limparFiltros = () => {
        setFiltros({ status: 'todos', cidade: '', provincia: '' });
        setSearchTerm('');
        setShowFiltros(false);
    };

    // ==============================================
    // CARREGAR DADOS INICIAIS
    // ==============================================
    useEffect(() => {
        (async () => {
            await fetchLoggedUser();
            await fetchFornecedores();
        })();
    }, []);

    // ==============================================
    // HANDLERS
    // ==============================================
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const resetForm = () => {
        setFormData({
            nome: '', email: '', endereco: '', nuit: '',
            telefone: '', cidade: '', provincia: '', ativo: true
        });
        setCreateMessage({ text: '', type: '' });
    };

    // ==============================================
    // CRIAÇÃO
    // ==============================================
    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setCreateLoading(true);
        setCreateMessage({ text: '', type: '' });

        if (!formData.nome.trim()) {
            setCreateMessage({ text: 'O nome é obrigatório', type: 'danger' });
            setCreateLoading(false);
            return;
        }

        let empresaParaEnviar = empresaId || (loggedUser && obterEmpresaIdDoUtilizador(loggedUser));
        if (!empresaParaEnviar) {
            setCreateMessage({ text: 'Utilizador sem empresa associada.', type: 'danger' });
            setCreateLoading(false);
            return;
        }

        const dadosParaEnviar = {
            nome: formData.nome.trim(),
            email: formData.email?.trim() || '',
            endereco: formData.endereco?.trim() || '',
            nuit: formData.nuit?.trim() || null,
            telefone: formData.telefone?.trim() || '',
            cidade: formData.cidade?.trim() || '',
            provincia: formData.provincia?.trim() || '',
            empresaId: empresaParaEnviar,
            ativo: formData.ativo !== undefined ? formData.ativo : true
        };

        try {
            const response = await api.post('/fornecedores', dadosParaEnviar);
            if (response.data) {
                setCreateMessage({ text: 'Fornecedor criado com sucesso!', type: 'success' });
                setTimeout(() => {
                    setShowCreateModal(false);
                    resetForm();
                    fetchFornecedores();
                }, 1500);
            }
        } catch (err) {
            setCreateMessage({
                text: err.response?.data?.erro || 'Erro ao criar fornecedor',
                type: 'danger'
            });
        } finally {
            setCreateLoading(false);
        }
    };

    // ==============================================
    // VER DETALHES
    // ==============================================
    const verDetalhes = async (fornecedor) => {
        try {
            setShowEditModal(false);
            setEditFormData(null);
            setDashboardFornecedor(null);
            setDashboardErro(null);
            setPaginaProdutos(1);
            setPaginaCompras(1);

            const response = await api.get(`/fornecedores/${fornecedor._id}`);
            setFornecedorDetalhes(response.data.data || fornecedor);
            setShowDetailsModal(true);
            await fetchDashboardFornecedor(fornecedor._id, false);
        } catch (err) {
            console.error('❌ Erro ao buscar detalhes:', err);
            setFornecedorDetalhes(fornecedor);
            setShowDetailsModal(true);
        }
    };

    // ==============================================
    // ABRIR MODAL DE EDIÇÃO DE COMPRA
    // ==============================================
    const abrirEditarCompra = (compra) => {
        if (compra.podeEditar === false) {
            const desc = descricaoBloqueio(compra);
            showTempToast('Bloqueado', desc, 'warning');
            return;
        }

        setCompraEditando(compra);
        setEditCompraForm({
            documentoFiscal: compra.documentoFiscal || '',
            metodoPagamento: compra.metodoPagamento || '',
            referenciaPagamento: compra.referenciaPagamento || '',
            observacoes: compra.observacoes || '',
            confirmarPagamento: compra.statusPagamento === 'Pago'
        });
        setEditCompraMsg({ text: '', type: '' });
        setShowEditCompraModal(true);
    };

    const fecharEditarCompra = () => {
        setShowEditCompraModal(false);
        setCompraEditando(null);
        setEditCompraMsg({ text: '', type: '' });
    };

    // ==============================================
    // GUARDAR EDIÇÃO DE COMPRA
    // 🔥 Após guardar, mantém a página atual ao recarregar
    // ==============================================
    const handleGuardarCompra = async (e) => {
        e.preventDefault();
        if (!compraEditando) return;

        setEditCompraLoading(true);
        setEditCompraMsg({ text: '', type: '' });

        try {
            const payload = {
                documentoFiscal: editCompraForm.documentoFiscal?.trim() || '',
                metodoPagamento: editCompraForm.metodoPagamento || '',
                referenciaPagamento: editCompraForm.referenciaPagamento?.trim() || '',
                observacoes: editCompraForm.observacoes?.trim() || ''
            };

            if (editCompraForm.confirmarPagamento) {
                payload.statusPagamento = 'Pago';
                payload.confirmarPagamento = true;
            } else if (compraEditando.statusPagamento === 'Pago') {
                payload.statusPagamento = 'Pendente';
            }

            const response = await api.put(`/compras-fornecedor/${compraEditando._id}`, payload);

            if (response.data?.success) {
                setEditCompraMsg({
                    text: 'Compra atualizada com sucesso!',
                    type: 'success'
                });

                setTimeout(async () => {
                    fecharEditarCompra();
                    if (fornecedorDetalhes) {
                        // 🔥 Recarrega sem resetar as páginas atuais
                        await fetchDashboardFornecedor(fornecedorDetalhes._id, false);
                    }
                }, 1200);
            } else {
                setEditCompraMsg({
                    text: response.data?.erro || 'Erro ao atualizar',
                    type: 'danger'
                });
            }
        } catch (err) {
            console.error('❌ Erro ao guardar compra:', err);
            setEditCompraMsg({
                text: err.response?.data?.erro || err.message || 'Erro ao atualizar compra',
                type: 'danger'
            });
        } finally {
            setEditCompraLoading(false);
        }
    };

    // ==============================================
    // EDIÇÃO DE FORNECEDOR
    // ==============================================
    const loadFornecedorForEdit = async (fornecedor) => {
        setEditLoading(true);
        try {
            setShowDetailsModal(false);
            setFornecedorDetalhes(null);

            const response = await api.get(`/fornecedores/${fornecedor._id}`);
            const f = response.data.data || fornecedor;

            setEditFormData({
                _id: f._id,
                nome: f.nome || '',
                email: f.email || '',
                endereco: f.endereco || '',
                nuit: f.nuit || '',
                telefone: f.telefone || '',
                cidade: f.cidade || '',
                provincia: f.provincia || '',
                ativo: f.ativo !== undefined ? f.ativo : true
            });

            setTimeout(() => setShowEditModal(true), 100);
        } catch (error) {
            console.error('Erro ao carregar fornecedor:', error);
            setEditMessage({ text: 'Erro ao carregar dados', type: 'danger' });
        } finally {
            setEditLoading(false);
        }
    };

    const handleEdit = (fornecedor) => loadFornecedorForEdit(fornecedor);

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditLoading(true);
        setEditMessage({ text: '', type: '' });

        if (!editFormData.nome.trim()) {
            setEditMessage({ text: 'Nome obrigatório', type: 'danger' });
            setEditLoading(false);
            return;
        }

        try {
            const { _id, ...updateData } = editFormData;
            const response = await api.put(`/fornecedores/${_id}`, updateData);

            if (response.data) {
                setEditMessage({ text: 'Fornecedor atualizado!', type: 'success' });
                setTimeout(() => {
                    setShowEditModal(false);
                    setEditFormData(null);
                    fetchFornecedores();
                }, 1500);
            }
        } catch (err) {
            setEditMessage({
                text: err.response?.data?.erro || 'Erro ao atualizar',
                type: 'danger'
            });
        } finally {
            setEditLoading(false);
        }
    };

    // ==============================================
    // AÇÕES RÁPIDAS
    // ==============================================
    const abrirWhatsApp = (f) => {
        const numero = f.telefone?.replace(/\D/g, '');
        if (numero) window.open(`https://wa.me/${numero}`, '_blank');
        else showTempToast('Aviso', 'Fornecedor sem telefone', 'warning');
    };

    const abrirEmail = (f) => {
        if (f.email) window.open(`mailto:${f.email}`, '_blank');
        else showTempToast('Aviso', 'Fornecedor sem email', 'warning');
    };

    // ==============================================
    // EXPORTAÇÃO
    // ==============================================
    const exportarFornecedores = async (formato) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) { showTempToast('Erro', 'Sessão expirada', 'danger'); return; }

            const params = new URLSearchParams();
            params.append('formato', formato);
            if (searchTerm) params.append('search', searchTerm);
            if (filtros.status && filtros.status !== 'todos') params.append('status', filtros.status);
            if (filtros.cidade) params.append('cidade', filtros.cidade);
            if (filtros.provincia) params.append('provincia', filtros.provincia);

            const url = `/api/fornecedores/exportar?${params.toString()}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.erro || 'Erro na exportação');
            }

            const blob = await response.blob();
            const extensao = formato === 'excel' || formato === 'xlsx' ? 'xlsx' : 'csv';

            const urlDownload = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = urlDownload;
            link.download = `fornecedores_${new Date().toISOString().split('T')[0]}.${extensao}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(urlDownload);

            showTempToast('Sucesso', `Exportado em ${formato.toUpperCase()}`, 'success');
        } catch (err) {
            showTempToast('Erro', err.message || 'Falha na exportação', 'danger');
        }
    };

    // ==============================================
    // EXCLUSÃO
    // ==============================================
    const openDeleteModal = (f) => { setFornecedorToDelete(f); setShowDeleteModal(true); };
    const closeDeleteModal = () => { setShowDeleteModal(false); setFornecedorToDelete(null); };

    const confirmDelete = async () => {
        if (!fornecedorToDelete) return;
        try {
            await api.delete(`/fornecedores/${fornecedorToDelete._id}`);
            showTempToast('Sucesso', `"${fornecedorToDelete.nome}" removido!`, 'success');
            closeDeleteModal();
            fetchFornecedores();
        } catch (err) {
            showTempToast('Erro', err.response?.data?.erro || 'Erro ao remover', 'danger');
        }
    };

    // ==============================================
    // PAGINAÇÃO — Lista principal
    // ==============================================
    const getCurrentPageFornecedores = () => {
        const start = (pagination.page - 1) * pagination.limit;
        return filteredFornecedores.slice(start, start + pagination.limit);
    };

    const goToPage = (page) => {
        if (page < 1 || page > pagination.pages) return;
        setPagination({ ...pagination, page });
    };

    // ==============================================
    // 🔥 PAGINAÇÃO — Produtos do dashboard
    // ==============================================
    const getProdutosPaginados = () => {
        if (!dashboardFornecedor?.produtos) return [];
        const start = (paginaProdutos - 1) * PRODUTOS_POR_PAGINA;
        return dashboardFornecedor.produtos.slice(start, start + PRODUTOS_POR_PAGINA);
    };

    const totalPaginasProdutos = () => {
        if (!dashboardFornecedor?.produtos) return 1;
        return Math.max(1, Math.ceil(dashboardFornecedor.produtos.length / PRODUTOS_POR_PAGINA));
    };

    const irParaPaginaProdutos = (pagina) => {
        const total = totalPaginasProdutos();
        if (pagina < 1 || pagina > total) return;
        setPaginaProdutos(pagina);
    };

    // ==============================================
    // 🔥 PAGINAÇÃO — Compras do dashboard
    // ==============================================
    const getComprasPaginadas = () => {
        if (!dashboardFornecedor?.compras) return [];
        const start = (paginaCompras - 1) * COMPRAS_POR_PAGINA;
        return dashboardFornecedor.compras.slice(start, start + COMPRAS_POR_PAGINA);
    };

    const totalPaginasCompras = () => {
        if (!dashboardFornecedor?.compras) return 1;
        return Math.max(1, Math.ceil(dashboardFornecedor.compras.length / COMPRAS_POR_PAGINA));
    };

    const irParaPaginaCompras = (pagina) => {
        const total = totalPaginasCompras();
        if (pagina < 1 || pagina > total) return;
        setPaginaCompras(pagina);
    };

    // ==============================================
    // RENDER — Paginação principal
    // ==============================================
    const renderPagination = () => {
        const { page, pages, total } = pagination;
        if (pages <= 1) return null;
        const startItem = (page - 1) * pagination.limit + 1;
        const endItem = Math.min(page * pagination.limit, total);

        return (
            <div className="fornecedores-pagination">
                <div className="pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {total}</span>
                </div>
                <div className="pagination-controls">
                    <button className="pagination-btn" onClick={() => goToPage(page - 1)} disabled={page === 1}>
                        <Icon name="chevron_left" size={18} />
                    </button>
                    <div className="pagination-numbers">
                        {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                            <button key={p}
                                className={`pagination-number ${page === p ? 'active' : ''}`}
                                onClick={() => goToPage(p)}>
                                {p}
                            </button>
                        ))}
                    </div>
                    <button className="pagination-btn" onClick={() => goToPage(page + 1)} disabled={page === pages}>
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // ==============================================
    // 🔥 RENDER — Paginação compacta (usada 2x no dashboard)
    // ==============================================
    const renderPaginacaoDashboard = (paginaAtual, totalPaginas, irPara, totalItens, itensPorPagina, label) => {
        if (totalPaginas <= 1) return null;
        const inicio = (paginaAtual - 1) * itensPorPagina + 1;
        const fim = Math.min(paginaAtual * itensPorPagina, totalItens);

        return (
            <div className="fdx-paginacao">
                <span className="fdx-paginacao-info">
                    {inicio}–{fim} de {totalItens}
                </span>
                <div className="fdx-paginacao-controles">
                    <button
                        className="fdx-paginacao-btn"
                        onClick={() => irPara(paginaAtual - 1)}
                        disabled={paginaAtual === 1}
                        title="Anterior"
                    >
                        <Icon name="chevron_left" size={14} />
                    </button>
                    <span className="fdx-paginacao-atual">
                        {paginaAtual} / {totalPaginas}
                    </span>
                    <button
                        className="fdx-paginacao-btn"
                        onClick={() => irPara(paginaAtual + 1)}
                        disabled={paginaAtual === totalPaginas}
                        title="Próxima"
                    >
                        <Icon name="chevron_right" size={14} />
                    </button>
                </div>
            </div>
        );
    };

    // ==============================================
    // CLASSES CSS DO STATUS
    // ==============================================
    const getStatusClass = (status) => {
        const s = String(status || '').toLowerCase();
        if (s === 'pago') return 'fdx-status-pago';
        if (s === 'parcial') return 'fdx-status-parcial';
        if (s === 'anulado') return 'fdx-status-anulado';
        return 'fdx-status-pendente';
    };

    // ==============================================
    // RENDER
    // ==============================================
    const currentFornecedores = getCurrentPageFornecedores();
    const produtosPaginados = getProdutosPaginados();
    const comprasPaginadas = getComprasPaginadas();

    return (
        <div className="fornecedores-container">

            {/* ============================================ */}
            {/* MODAL DE CRIAÇÃO */}
            {/* ============================================ */}
            {showCreateModal && (
                <div className="fornecedor-modal-overlay" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                    <div className="fornecedor-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="fornecedor-modal-header">
                            <h3><Icon name="person_add" size={22} /> Novo Fornecedor</h3>
                            <button className="fornecedor-modal-close" onClick={() => { setShowCreateModal(false); resetForm(); }}>×</button>
                        </div>
                        <div className="fornecedor-modal-body">
                            {createMessage.text && (
                                <div className={`fornecedor-modal-alert fornecedor-modal-alert-${createMessage.type}`}>
                                    <Icon name={createMessage.type === 'success' ? 'check_circle' : 'error'} size={18} />
                                    <span>{createMessage.text}</span>
                                    <button className="fornecedor-alert-close" onClick={() => setCreateMessage({ text: '', type: '' })}>×</button>
                                </div>
                            )}
                            <form onSubmit={handleCreateSubmit}>
                                <div className="fornecedor-modal-section">
                                    <div className="fornecedor-form-group">
                                        <label>Nome <span className="fornecedor-required">*</span></label>
                                        <input type="text" name="nome" value={formData.nome} onChange={handleChange} required placeholder="Nome do fornecedor" />
                                    </div>
                                    <div className="fornecedor-form-row">
                                        <div className="fornecedor-form-group">
                                            <label>Email</label>
                                            <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="fornecedor@email.com" />
                                        </div>
                                        <div className="fornecedor-form-group">
                                            <label>Telefone</label>
                                            <input type="tel" name="telefone" value={formData.telefone} onChange={handleChange} placeholder="+258 84 123 4567" />
                                        </div>
                                    </div>
                                    <div className="fornecedor-form-row">
                                        <div className="fornecedor-form-group">
                                            <label>NUIT</label>
                                            <input type="text" name="nuit" value={formData.nuit} onChange={handleChange} placeholder="NUIT" />
                                        </div>
                                        <div className="fornecedor-form-group">
                                            <label>Cidade</label>
                                            <input type="text" name="cidade" value={formData.cidade} onChange={handleChange} placeholder="Cidade" />
                                        </div>
                                    </div>
                                    <div className="fornecedor-form-row">
                                        <div className="fornecedor-form-group">
                                            <label>Província</label>
                                            <input type="text" name="provincia" value={formData.provincia} onChange={handleChange} placeholder="Província" />
                                        </div>
                                        <div className="fornecedor-form-group">
                                            <label>Endereço</label>
                                            <input type="text" name="endereco" value={formData.endereco} onChange={handleChange} placeholder="Endereço completo" />
                                        </div>
                                    </div>
                                    <div className="fornecedor-form-group fornecedor-checkbox-group">
                                        <label className="fornecedor-checkbox-label">
                                            <input type="checkbox" name="ativo"
                                                checked={formData.ativo !== undefined ? formData.ativo : true}
                                                onChange={handleChange} />
                                            <span>Fornecedor Ativo</span>
                                        </label>
                                    </div>
                                </div>
                                <div className="fornecedor-modal-actions">
                                    <button type="submit" className="fornecedor-modal-btn-primary" disabled={createLoading}>
                                        {createLoading ? 'A criar...' : 'Criar Fornecedor'}
                                    </button>
                                    <button type="button" className="fornecedor-modal-btn-secondary" onClick={resetForm}>Limpar</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================ */}
            {/* MODAL DE EDIÇÃO DE FORNECEDOR */}
            {/* ============================================ */}
            {showEditModal && editFormData && (
                <div className="fornecedor-modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="fornecedor-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="fornecedor-modal-header">
                            <h3><Icon name="edit" size={22} /> Editar Fornecedor</h3>
                            <button className="fornecedor-modal-close" onClick={() => setShowEditModal(false)}>×</button>
                        </div>
                        <div className="fornecedor-modal-body">
                            {editMessage.text && (
                                <div className={`fornecedor-modal-alert fornecedor-modal-alert-${editMessage.type}`}>
                                    <Icon name={editMessage.type === 'success' ? 'check_circle' : 'error'} size={18} />
                                    <span>{editMessage.text}</span>
                                    <button className="fornecedor-alert-close" onClick={() => setEditMessage({ text: '', type: '' })}>×</button>
                                </div>
                            )}
                            <form onSubmit={handleEditSubmit}>
                                <div className="fornecedor-modal-section">
                                    <div className="fornecedor-form-group">
                                        <label>Nome <span className="fornecedor-required">*</span></label>
                                        <input type="text" name="nome" value={editFormData.nome} onChange={handleEditChange} required />
                                    </div>
                                    <div className="fornecedor-form-row">
                                        <div className="fornecedor-form-group">
                                            <label>Email</label>
                                            <input type="email" name="email" value={editFormData.email} onChange={handleEditChange} />
                                        </div>
                                        <div className="fornecedor-form-group">
                                            <label>Telefone</label>
                                            <input type="tel" name="telefone" value={editFormData.telefone} onChange={handleEditChange} />
                                        </div>
                                    </div>
                                    <div className="fornecedor-form-row">
                                        <div className="fornecedor-form-group">
                                            <label>NUIT</label>
                                            <input type="text" name="nuit" value={editFormData.nuit} onChange={handleEditChange} />
                                        </div>
                                        <div className="fornecedor-form-group">
                                            <label>Cidade</label>
                                            <input type="text" name="cidade" value={editFormData.cidade} onChange={handleEditChange} />
                                        </div>
                                    </div>
                                    <div className="fornecedor-form-row">
                                        <div className="fornecedor-form-group">
                                            <label>Província</label>
                                            <input type="text" name="provincia" value={editFormData.provincia} onChange={handleEditChange} />
                                        </div>
                                        <div className="fornecedor-form-group">
                                            <label>Endereço</label>
                                            <input type="text" name="endereco" value={editFormData.endereco} onChange={handleEditChange} />
                                        </div>
                                    </div>
                                    <div className="fornecedor-form-group fornecedor-checkbox-group">
                                        <label className="fornecedor-checkbox-label">
                                            <input type="checkbox" name="ativo"
                                                checked={editFormData.ativo !== undefined ? editFormData.ativo : true}
                                                onChange={handleEditChange} />
                                            <span>Fornecedor Ativo</span>
                                        </label>
                                    </div>
                                </div>
                                <div className="fornecedor-modal-actions">
                                    <button type="submit" className="fornecedor-modal-btn-primary" disabled={editLoading}>
                                        {editLoading ? 'A atualizar...' : 'Salvar Alterações'}
                                    </button>
                                    <button type="button" className="fornecedor-modal-btn-secondary" onClick={() => setShowEditModal(false)}>Cancelar</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================ */}
            {/* MODAL DE DETALHES COM DASHBOARD */}
            {/* ============================================ */}
            {showDetailsModal && fornecedorDetalhes && (
                <div className="fornecedor-modal-overlay" onClick={() => setShowDetailsModal(false)}>
                    <div className="fornecedor-modal-content fornecedor-modal-large fornecedor-modal-dashboard"
                        onClick={(e) => e.stopPropagation()}>

                        <div className="fornecedor-modal-header">
                            <h3>
                                <Icon name="local_shipping" size={22} />
                                {fornecedorDetalhes.nome}
                                <span className={`fornecedor-status-badge ${fornecedorDetalhes.ativo !== false ? 'fornecedor-status-active' : 'fornecedor-status-inactive'}`}
                                    style={{ marginLeft: 8, fontSize: '0.65rem' }}>
                                    {fornecedorDetalhes.ativo !== false ? '🟢 Ativo' : '🔴 Inativo'}
                                </span>
                            </h3>
                            <button className="fornecedor-modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
                        </div>

                        <div className="fornecedor-modal-body">
                            {loadingDashboard ? (
                                <div className="fornecedor-dashboard-loading">
                                    <div className="spinner-large"></div>
                                    <p>A carregar dados...</p>
                                </div>
                            ) : dashboardFornecedor ? (
                                <>
                                    {/* AVISOS DE EDITABILIDADE */}
                                    {dashboardFornecedor.editabilidade &&
                                        (dashboardFornecedor.editabilidade.totalSemNF > 0 ||
                                         dashboardFornecedor.editabilidade.totalComNFSemPagamento > 0) && (
                                        <div className="fdx-avisos-topo">
                                            {dashboardFornecedor.editabilidade.totalSemNF > 0 && (
                                                <div className="fdx-aviso fdx-aviso-laranja">
                                                    <Icon name="warning" size={16} />
                                                    <span>
                                                        <strong>{dashboardFornecedor.editabilidade.totalSemNF}</strong> compra(s) sem Nota Fiscal
                                                    </span>
                                                </div>
                                            )}
                                            {dashboardFornecedor.editabilidade.totalComNFSemPagamento > 0 && (
                                                <div className="fdx-aviso fdx-aviso-azul">
                                                    <Icon name="info" size={16} />
                                                    <span>
                                                        <strong>{dashboardFornecedor.editabilidade.totalComNFSemPagamento}</strong> compra(s) com NF mas por pagar
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* KPIs */}
                                    <div className="fornecedor-dashboard-kpis">
                                        <div className="fdx-kpi fdx-kpi-azul">
                                            <div className="fdx-kpi-icon"><Icon name="shopping_cart" size={20} /></div>
                                            <div className="fdx-kpi-info">
                                                <span className="fdx-kpi-label">Total Compras</span>
                                                <span className="fdx-kpi-valor">{formatarMoeda(dashboardFornecedor.kpis.totalCompras)}</span>
                                                <small>{dashboardFornecedor.kpis.numeroCompras} compras</small>
                                            </div>
                                        </div>
                                        <div className="fdx-kpi fdx-kpi-verde">
                                            <div className="fdx-kpi-icon"><Icon name="payments" size={20} /></div>
                                            <div className="fdx-kpi-info">
                                                <span className="fdx-kpi-label">Pago</span>
                                                <span className="fdx-kpi-valor">{formatarMoeda(dashboardFornecedor.kpis.totalPago)}</span>
                                                <small>total liquidado</small>
                                            </div>
                                        </div>
                                        <div className="fdx-kpi fdx-kpi-laranja">
                                            <div className="fdx-kpi-icon"><Icon name="warning" size={20} /></div>
                                            <div className="fdx-kpi-info">
                                                <span className="fdx-kpi-label">A Pagar</span>
                                                <span className="fdx-kpi-valor">{formatarMoeda(dashboardFornecedor.kpis.totalAPagar)}</span>
                                                <small>saldo em aberto</small>
                                            </div>
                                        </div>
                                        <div className="fdx-kpi fdx-kpi-roxo">
                                            <div className="fdx-kpi-icon"><Icon name="inventory_2" size={20} /></div>
                                            <div className="fdx-kpi-info">
                                                <span className="fdx-kpi-label">Produtos</span>
                                                <span className="fdx-kpi-valor">{dashboardFornecedor.kpis.numeroProdutos}</span>
                                                <small>associados</small>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Info básica */}
                                    <div className="fornecedor-dashboard-info">
                                        <div className="fdx-info-item">
                                            <span className="fdx-info-label">📧 Email</span>
                                            <span className="fdx-info-value">{dashboardFornecedor.fornecedor.email || '-'}</span>
                                        </div>
                                        <div className="fdx-info-item">
                                            <span className="fdx-info-label">📞 Telefone</span>
                                            <span className="fdx-info-value">{dashboardFornecedor.fornecedor.telefone || '-'}</span>
                                        </div>
                                        <div className="fdx-info-item">
                                            <span className="fdx-info-label">📌 NUIT</span>
                                            <span className="fdx-info-value">{dashboardFornecedor.fornecedor.nuit || '-'}</span>
                                        </div>
                                        <div className="fdx-info-item">
                                            <span className="fdx-info-label">📍 Cidade</span>
                                            <span className="fdx-info-value">{dashboardFornecedor.fornecedor.cidade || '-'}</span>
                                        </div>
                                    </div>

                                    {/* Gráficos */}
                                    <div className="fornecedor-dashboard-graficos">
                                        <div className="fdx-card">
                                            <h4 className="fdx-card-title">
                                                <Icon name="show_chart" size={16} /> Compras (últimos 12 meses)
                                            </h4>
                                            {dashboardFornecedor.evolucao.every(e => e.total === 0) ? (
                                                <p className="fdx-vazio">Sem compras nos últimos 12 meses</p>
                                            ) : (
                                                <ResponsiveContainer width="100%" height={220}>
                                                    <BarChart data={dashboardFornecedor.evolucao}>
                                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                        <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                                                        <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                                                        <Tooltip formatter={(v) => formatarMoeda(v)} contentStyle={{ fontSize: '0.75rem', borderRadius: 8 }} />
                                                        <Bar dataKey="total" fill="#3b82f6" name="Total" radius={[6, 6, 0, 0]} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>
                                        <div className="fdx-card">
                                            <h4 className="fdx-card-title">
                                                <Icon name="pie_chart" size={16} /> Produtos por Categoria
                                            </h4>
                                            {dashboardFornecedor.distribuicaoCategorias.length === 0 ? (
                                                <p className="fdx-vazio">Sem produtos associados</p>
                                            ) : (
                                                <ResponsiveContainer width="100%" height={220}>
                                                    <PieChart>
                                                        <Pie data={dashboardFornecedor.distribuicaoCategorias}
                                                            dataKey="quantidade" nameKey="nome"
                                                            cx="50%" cy="50%"
                                                            outerRadius="70%" innerRadius="45%"
                                                            paddingAngle={2}
                                                            label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : null}>
                                                            {dashboardFornecedor.distribuicaoCategorias.map((_, i) => (
                                                                <Cell key={i} fill={CORES_CATEGORIAS[i % CORES_CATEGORIAS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip formatter={(v) => `${v} produto(s)`} />
                                                        <Legend verticalAlign="bottom" height={30} iconType="circle" iconSize={8}
                                                            wrapperStyle={{ fontSize: '0.7rem' }} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>
                                    </div>

                                    {/* 🔥 PRODUTOS — COM PAGINAÇÃO */}
                                    <div className="fdx-section">
                                        <h4 className="fdx-section-title">
                                            <Icon name="inventory_2" size={16} />
                                            Produtos Associados
                                            <span className="fdx-badge">{dashboardFornecedor.produtos.length}</span>
                                        </h4>
                                        {dashboardFornecedor.produtos.length === 0 ? (
                                            <p className="fdx-vazio">Nenhum produto associado</p>
                                        ) : (
                                            <>
                                                <div className="fdx-table-wrapper">
                                                    <table className="fdx-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Produto</th>
                                                                <th>Categoria</th>
                                                                <th>Stock</th>
                                                                <th>Preço Compra</th>
                                                                <th>Preço Venda</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {produtosPaginados.map(p => (
                                                                <tr key={p._id}>
                                                                    <td>
                                                                        <strong>{p.nome}</strong>
                                                                        {p.codigo && <small className="fdx-codigo">{p.codigo}</small>}
                                                                    </td>
                                                                    <td><span className="fdx-cat-badge">{p.categoria}</span></td>
                                                                    <td>
                                                                        <span className={`fdx-stock ${p.stockAtual <= p.stockMinimo ? 'fdx-stock-baixo' : ''}`}>
                                                                            {p.stockAtual} {p.unidadeMedida}
                                                                        </span>
                                                                    </td>
                                                                    <td>{formatarMoeda(p.precoCompra)}</td>
                                                                    <td>{formatarMoeda(p.precoVenda)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                {/* 🔥 Paginação dos produtos */}
                                                {renderPaginacaoDashboard(
                                                    paginaProdutos,
                                                    totalPaginasProdutos(),
                                                    irParaPaginaProdutos,
                                                    dashboardFornecedor.produtos.length,
                                                    PRODUTOS_POR_PAGINA,
                                                    'produtos'
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* 🔥 COMPRAS — COM PAGINAÇÃO */}
                                    <div className="fdx-section">
                                        <h4 className="fdx-section-title">
                                            <Icon name="receipt_long" size={16} />
                                            Compras a este Fornecedor
                                            <span className="fdx-badge">{dashboardFornecedor.compras.length}</span>
                                        </h4>
                                        {dashboardFornecedor.compras.length === 0 ? (
                                            <p className="fdx-vazio">Sem compras registadas</p>
                                        ) : (
                                            <>
                                                <div className="fdx-table-wrapper">
                                                    <table className="fdx-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Nº</th>
                                                                <th>Data</th>
                                                                <th>Doc.</th>
                                                                <th>Total</th>
                                                                <th>Pago</th>
                                                                <th>Saldo</th>
                                                                <th>Estado</th>
                                                                <th>Ações</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {comprasPaginadas.map(c => (
                                                                <tr key={c._id}>
                                                                    <td><strong>{c.numero}</strong></td>
                                                                    <td>{formatarData(c.dataCompra)}</td>
                                                                    <td>
                                                                        {c.documentoFiscal ? (
                                                                            <span>{c.documentoFiscal}</span>
                                                                        ) : (
                                                                            <span className="fdx-badge-sem-nf">
                                                                                ⚠️ Sem NF
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td>{formatarMoeda(c.total)}</td>
                                                                    <td>{formatarMoeda(c.valorPago)}</td>
                                                                    <td>{formatarMoeda(c.saldoDevedor)}</td>
                                                                    <td>
                                                                        <span className={`fdx-status ${getStatusClass(c.statusPagamento)}`}>
                                                                            {c.statusPagamento}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        {c.podeEditar ? (
                                                                            <button
                                                                                className="fdx-btn-edit-compra"
                                                                                onClick={() => abrirEditarCompra(c)}
                                                                                title={
                                                                                    c.motivoBloqueio === 'sem_nf' ? 'Adicionar Nota Fiscal' :
                                                                                    c.motivoBloqueio === 'nf_sem_pagamento' ? 'Confirmar Pagamento' :
                                                                                    'Editar Compra'
                                                                                }
                                                                            >
                                                                                <Icon name="edit" size={14} />
                                                                            </button>
                                                                        ) : (
                                                                            <span
                                                                                className="fdx-btn-bloqueado"
                                                                                title={descricaoBloqueio(c)}
                                                                            >
                                                                                <Icon name="lock" size={14} />
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                {/* 🔥 Paginação das compras */}
                                                {renderPaginacaoDashboard(
                                                    paginaCompras,
                                                    totalPaginasCompras(),
                                                    irParaPaginaCompras,
                                                    dashboardFornecedor.compras.length,
                                                    COMPRAS_POR_PAGINA,
                                                    'compras'
                                                )}
                                            </>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="fornecedor-detalhes-grid">
                                    <div className="fornecedor-info-item">
                                        <span className="fornecedor-label">📧 Email</span>
                                        <span className="fornecedor-value">{fornecedorDetalhes.email || '-'}</span>
                                    </div>
                                    <div className="fornecedor-info-item">
                                        <span className="fornecedor-label">📞 Telefone</span>
                                        <span className="fornecedor-value">{fornecedorDetalhes.telefone || '-'}</span>
                                    </div>
                                    <div className="fornecedor-info-item">
                                        <span className="fornecedor-label">📍 Endereço</span>
                                        <span className="fornecedor-value">{fornecedorDetalhes.endereco || '-'}</span>
                                    </div>
                                    <div className="fornecedor-info-item">
                                        <span className="fornecedor-label">🏙️ Cidade</span>
                                        <span className="fornecedor-value">{fornecedorDetalhes.cidade || '-'}</span>
                                    </div>
                                    <div className="fornecedor-info-item">
                                        <span className="fornecedor-label">🗺️ Província</span>
                                        <span className="fornecedor-value">{fornecedorDetalhes.provincia || '-'}</span>
                                    </div>
                                    <div className="fornecedor-info-item">
                                        <span className="fornecedor-label">📌 NUIT</span>
                                        <span className="fornecedor-value">{fornecedorDetalhes.nuit || '-'}</span>
                                    </div>
                                    <p className="fdx-vazio" style={{ gridColumn: '1 / -1', color: '#ef4444' }}>
                                        ⚠️ {dashboardErro || 'Não foi possível carregar o dashboard.'}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="fornecedor-modal-footer">
                            <button className="fornecedor-btn-primary" onClick={() => handleEdit(fornecedorDetalhes)}>
                                <Icon name="edit" size={16} /> Editar Fornecedor
                            </button>
                            <button className="fornecedor-btn-secondary" onClick={() => setShowDetailsModal(false)}>
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================ */}
            {/* MODAL DE EDIÇÃO DE COMPRA */}
            {/* ============================================ */}
            {showEditCompraModal && compraEditando && (
                <div className="fornecedor-modal-overlay" onClick={fecharEditarCompra}
                    style={{ zIndex: 1100 }}>
                    <div className="fornecedor-modal-content fornecedor-modal-compra-edit"
                        onClick={(e) => e.stopPropagation()}>

                        <div className="fornecedor-modal-header">
                            <h3>
                                <Icon name="receipt_long" size={22} />
                                {tituloModalEdicao(compraEditando)} <strong>{compraEditando.numero}</strong>
                            </h3>
                            <button className="fornecedor-modal-close" onClick={fecharEditarCompra}>×</button>
                        </div>

                        <div className="fornecedor-modal-body">
                            {editCompraMsg.text && (
                                <div className={`fornecedor-modal-alert fornecedor-modal-alert-${editCompraMsg.type}`}>
                                    <Icon name={editCompraMsg.type === 'success' ? 'check_circle' : 'error'} size={18} />
                                    <span>{editCompraMsg.text}</span>
                                    <button className="fornecedor-alert-close"
                                        onClick={() => setEditCompraMsg({ text: '', type: '' })}>×</button>
                                </div>
                            )}

                            {compraEditando.motivoBloqueio === 'sem_nf' && (
                                <div className="compra-edit-aviso compra-edit-aviso-laranja">
                                    <Icon name="warning" size={16} />
                                    <span>
                                        Esta compra ainda não tem Nota Fiscal. Ao adicionar,
                                        o estado muda automaticamente para <strong>Pago</strong>.
                                    </span>
                                </div>
                            )}
                            {compraEditando.motivoBloqueio === 'nf_sem_pagamento' && (
                                <div className="compra-edit-aviso compra-edit-aviso-azul">
                                    <Icon name="info" size={16} />
                                    <span>
                                        Esta compra tem NF mas ainda está <strong>Pendente</strong>.
                                        Marque "Confirmar Pagamento" para liquidar.
                                    </span>
                                </div>
                            )}

                            <div className="compra-edit-resumo">
                                <div className="compra-edit-resumo-item">
                                    <span>Total:</span>
                                    <strong>{formatarMoeda(compraEditando.total)}</strong>
                                </div>
                                <div className="compra-edit-resumo-item">
                                    <span>Pago:</span>
                                    <strong>{formatarMoeda(compraEditando.valorPago)}</strong>
                                </div>
                                <div className="compra-edit-resumo-item">
                                    <span>Saldo:</span>
                                    <strong className="text-danger">{formatarMoeda(compraEditando.saldoDevedor)}</strong>
                                </div>
                                <div className="compra-edit-resumo-item">
                                    <span>Estado atual:</span>
                                    <span className={`fdx-status ${getStatusClass(compraEditando.statusPagamento)}`}>
                                        {compraEditando.statusPagamento}
                                    </span>
                                </div>
                            </div>

                            <form onSubmit={handleGuardarCompra}>
                                <div className="fornecedor-modal-section">
                                    <div className="fornecedor-form-group">
                                        <label>
                                            <Icon name="receipt" size={14} /> Nota Fiscal / Documento
                                        </label>
                                        <input
                                            type="text"
                                            value={editCompraForm.documentoFiscal}
                                            onChange={(e) => setEditCompraForm({
                                                ...editCompraForm,
                                                documentoFiscal: e.target.value
                                            })}
                                            placeholder="Ex: FT2026/00123"
                                        />
                                        <small className="fornecedor-form-help">
                                            Adicione ou corrija o número da fatura/nota fiscal
                                        </small>
                                    </div>

                                    <div className="fornecedor-form-row">
                                        <div className="fornecedor-form-group">
                                            <label>Método de Pagamento</label>
                                            <select
                                                value={editCompraForm.metodoPagamento}
                                                onChange={(e) => setEditCompraForm({
                                                    ...editCompraForm,
                                                    metodoPagamento: e.target.value
                                                })}
                                            >
                                                <option value="">— Selecione —</option>
                                                <option value="Dinheiro">💵 Dinheiro</option>
                                                <option value="Transferência">🏦 Transferência</option>
                                                <option value="Cheque">📝 Cheque</option>
                                                <option value="Cartão">💳 Cartão</option>
                                                <option value="M-Pesa">📱 M-Pesa</option>
                                                <option value="E-Mola">📱 E-Mola</option>
                                                <option value="Outro">🔹 Outro</option>
                                            </select>
                                        </div>
                                        <div className="fornecedor-form-group">
                                            <label>Referência</label>
                                            <input
                                                type="text"
                                                value={editCompraForm.referenciaPagamento}
                                                onChange={(e) => setEditCompraForm({
                                                    ...editCompraForm,
                                                    referenciaPagamento: e.target.value
                                                })}
                                                placeholder="Ex: TRF-2026-001"
                                            />
                                        </div>
                                    </div>

                                    <div className="fornecedor-form-group">
                                        <label>Observações</label>
                                        <textarea
                                            value={editCompraForm.observacoes}
                                            onChange={(e) => setEditCompraForm({
                                                ...editCompraForm,
                                                observacoes: e.target.value
                                            })}
                                            rows="3"
                                            placeholder="Notas internas sobre esta compra..."
                                        />
                                    </div>

                                    <div className={`compra-edit-confirmar-pagamento ${compraEditando.statusPagamento === 'Pago' ? 'compra-edit-confirmar-pago' : ''}`}>
                                        <label className="fornecedor-checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={editCompraForm.confirmarPagamento}
                                                onChange={(e) => setEditCompraForm({
                                                    ...editCompraForm,
                                                    confirmarPagamento: e.target.checked
                                                })}
                                            />
                                            <div className="compra-edit-confirmar-info">
                                                <strong>
                                                    {compraEditando.statusPagamento === 'Pago'
                                                        ? '💳 Compra liquidada'
                                                        : '✅ Confirmar pagamento'}
                                                </strong>
                                                <small>
                                                    {compraEditando.statusPagamento === 'Pago'
                                                        ? 'Desmarque para reverter para "Pendente".'
                                                        : 'Marque para mudar o estado para "Pago". O saldo devedor será zerado.'}
                                                </small>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div className="fornecedor-modal-actions">
                                    <button type="submit" className="fornecedor-modal-btn-primary"
                                        disabled={editCompraLoading}>
                                        {editCompraLoading ? 'A guardar...' : 'Guardar Alterações'}
                                    </button>
                                    <button type="button" className="fornecedor-modal-btn-secondary"
                                        onClick={fecharEditarCompra}>
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================ */}
            {/* MODAL DE EXCLUSÃO */}
            {/* ============================================ */}
            {showDeleteModal && fornecedorToDelete && (
                <div className="fornecedor-modal-overlay" onClick={closeDeleteModal}>
                    <div className="fornecedor-modal-content-popups fornecedor-modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="fornecedor-modal-header">
                            <h3><span className="fornecedor-header-icon">⚠️</span> Confirmar Exclusão</h3>
                            <button className="fornecedor-modal-close" onClick={closeDeleteModal}>×</button>
                        </div>
                        <div className="fornecedor-modal-body-exc">
                            <div className="fornecedor-confirm-icon warning">
                                <Icon name="warning" size={32} />
                            </div>
                            <h4>Remover fornecedor?</h4>
                            <p>Tem certeza que deseja remover <strong>{fornecedorToDelete.nome}</strong>?</p>
                            <p className="fornecedor-confirm-subtext">
                                <Icon name="info" size={14} /> Esta ação é irreversível.
                            </p>
                        </div>
                        <div className="fornecedor-modal-actions">
                            <button className="fornecedor-btn-confirm-danger" onClick={confirmDelete}>
                                <Icon name="delete_forever" size={16} /> Eliminar
                            </button>
                            <button className="fornecedor-btn-cancel" onClick={closeDeleteModal}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================ */}
            {/* HEADER */}
            {/* ============================================ */}
            <div className="fornecedores-header">
                <div className="header-title">
                    <h2><Icon name="local_shipping" size={24} /> Gestão de Fornecedores</h2>
                    <p>Gerencie os fornecedores da sua empresa</p>
                </div>
                <div className="header-actions">
                    <button className="btn-export" onClick={() => exportarFornecedores('excel')} title="Exportar Excel">
                        <Icon name="file_download" size={18} /> Excel
                    </button>
                    <button className="btn-export" onClick={() => exportarFornecedores('csv')} title="Exportar CSV">
                        <Icon name="file_download" size={18} /> CSV
                    </button>
                    <button className="btn-create" onClick={() => setShowCreateModal(true)}>
                        <Icon name="add" size={18} /> Novo Fornecedor
                    </button>
                </div>
            </div>

            {/* MENSAGENS */}
            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    <Icon name={message.type === 'success' ? 'check_circle' : 'error'} size={16} />
                    <span>{message.text}</span>
                    <button className="alert-close" onClick={() => setMessage({ text: '', type: '' })}>×</button>
                </div>
            )}

            {/* KPIs */}
            <div className="fornecedores-kpi-grid">
                <div className="kpi-card primary">
                    <div className="kpi-icon"><Icon name="local_shipping" size={22} /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.total}</span>
                        <span className="kpi-label">Total Fornecedores</span>
                    </div>
                </div>
                <div className="kpi-card success">
                    <div className="kpi-icon"><Icon name="check_circle" size={22} /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.ativos}</span>
                        <span className="kpi-label">Ativos</span>
                    </div>
                    <span className="kpi-change positive">
                        {stats.total > 0 ? Math.round((stats.ativos / stats.total) * 100) : 0}%
                    </span>
                </div>
                <div className="kpi-card warning">
                    <div className="kpi-icon"><Icon name="phone" size={22} /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.comTelefone}</span>
                        <span className="kpi-label">Com Telefone</span>
                    </div>
                </div>
                <div className="kpi-card info">
                    <div className="kpi-icon"><Icon name="email" size={22} /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.comEmail}</span>
                        <span className="kpi-label">Com Email</span>
                    </div>
                </div>
            </div>

            {/* FILTROS */}
            <div className="fornecedores-filters">
                <div className="search-box">
                    <Icon name="search" size={18} />
                    <input type="text"
                        placeholder="Pesquisar por nome, email, NUIT, telefone, cidade ou província..."
                        value={searchTerm || ''}
                        onChange={(e) => setSearchTerm(e.target.value)} />
                    {searchTerm && (
                        <button className="search-clear" onClick={() => setSearchTerm('')}>
                            <Icon name="close" size={16} />
                        </button>
                    )}
                </div>
                <div className="filter-actions">
                    <button className="btn-filter" onClick={() => setShowFiltros(!showFiltros)}>
                        <Icon name="filter_list" size={18} />
                        {showFiltros ? 'Ocultar Filtros' : 'Filtros'}
                    </button>
                    <button className="btn-refresh" onClick={fetchFornecedores} title="Atualizar">
                        <Icon name="refresh" size={18} />
                    </button>
                </div>
            </div>

            {/* FILTROS AVANÇADOS */}
            {showFiltros && (
                <div className="fornecedores-filtros-avancados">
                    <div className="filtros-row">
                        <div className="filtro-group">
                            <label>Status</label>
                            <select value={filtros.status}
                                onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}>
                                <option value="todos">Todos</option>
                                <option value="ativo">🟢 Ativo</option>
                                <option value="inativo">🔴 Inativo</option>
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Cidade</label>
                            <input type="text" placeholder="Filtrar por cidade"
                                value={filtros.cidade}
                                onChange={(e) => setFiltros({ ...filtros, cidade: e.target.value })} />
                        </div>
                        <div className="filtro-group">
                            <label>Província</label>
                            <input type="text" placeholder="Filtrar por província"
                                value={filtros.provincia}
                                onChange={(e) => setFiltros({ ...filtros, provincia: e.target.value })} />
                        </div>
                        <button className="btn-aplicar-filtros" onClick={filtrarFornecedores}>
                            <Icon name="filter_list" size={16} /> Aplicar
                        </button>
                        <button className="btn-limpar-filtros" onClick={limparFiltros}>
                            <Icon name="clear" size={16} /> Limpar
                        </button>
                    </div>
                </div>
            )}

            {/* TABELA */}
            {loading ? (
                <div className="loading-container">
                    <div className="spinner-large"></div>
                    <p>A carregar fornecedores...</p>
                </div>
            ) : (
                <div className="fornecedores-table-container">
                    <table className="fornecedores-table">
                        <thead>
                            <tr>
                                <th className="col-nome">Nome</th>
                                <th className="col-email">Email</th>
                                <th className="col-telefone">Telefone</th>
                                <th className="col-localizacao">Localização</th>
                                <th className="col-status">Status</th>
                                <th className="col-acoes">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentFornecedores.length === 0 ? (
                                <tr className="empty-row">
                                    <td colSpan="6">
                                        <div className="empty-state">
                                            <Icon name="local_shipping" size={48} />
                                            <p>Nenhum fornecedor encontrado</p>
                                            <button className="btn-create-small" onClick={() => setShowCreateModal(true)}>
                                                Criar primeiro fornecedor
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                currentFornecedores.map(fornecedor => {
                                    const localizacao = [fornecedor.cidade, fornecedor.provincia]
                                        .filter(Boolean).join(', ') || '-';
                                    const isAtivo = fornecedor.ativo !== false;

                                    return (
                                        <tr key={fornecedor._id} className={!isAtivo ? 'fornecedor-inativo' : ''}>
                                            <td>
                                                <div className="fornecedor-info">
                                                    <strong>{fornecedor.nome}</strong>
                                                    <div className="fornecedor-badges">
                                                        {fornecedor.nuit && (
                                                            <span className="badge-nuit">📋 {fornecedor.nuit}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{fornecedor.email || '-'}</td>
                                            <td>{fornecedor.telefone || '-'}</td>
                                            <td>{localizacao}</td>
                                            <td>
                                                <span className={`status-badge ${isAtivo ? 'status-active' : 'status-inactive'}`}>
                                                    <Icon name={isAtivo ? 'check_circle' : 'block'} size={12} />
                                                    {isAtivo ? 'Ativo' : 'Inativo'}
                                                </span>
                                            </td>
                                            <td className="actions-cell">
                                                <button className="action-btn view" onClick={() => verDetalhes(fornecedor)} title="Ver detalhes">
                                                    <Icon name="visibility" size={16} />
                                                </button>
                                                <button className="action-btn edit" onClick={() => handleEdit(fornecedor)} title="Editar">
                                                    <Icon name="edit" size={16} />
                                                </button>
                                                <button className="action-btn whatsapp" onClick={() => abrirWhatsApp(fornecedor)} title="WhatsApp">
                                                    <Icon name="whatsapp" size={16} style={{ color: '#25D366' }} />
                                                </button>
                                                <button className="action-btn email" onClick={() => abrirEmail(fornecedor)} title="Email">
                                                    <Icon name="email" size={16} style={{ color: '#EA4335' }} />
                                                </button>
                                                <button className="action-btn delete" onClick={() => openDeleteModal(fornecedor)} title="Excluir">
                                                    <Icon name="delete" size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                    {renderPagination()}
                </div>
            )}
        </div>
    );
}